import { applicationDocumentTypeSchema, publishedApplicationDocumentSchema, pickLocaleCopy, type AppContentLocale, type PublishedApplicationDocument } from "@yahshi/contracts";
import { Button, Alert, Result, Spin } from "antd";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { legalTypes } from "../components/contentLabels";
import { useOptionalContentApi } from "../data/ContentDistributionApi";
import { useAdminRuntime } from "../../../app/AdminRuntime";
import { resolveUserCenterApiBase } from "../../m1-app/data/appIntegration";
import { PublicLocaleSwitch } from "../../../shared/i18n/PublicLocaleSwitch";
import "../content.css";

async function fetchPublishedDocument(appId: string, type: string): Promise<PublishedApplicationDocument> {
  const parsed = applicationDocumentTypeSchema.parse(type);
  const response = await fetch(`${resolveUserCenterApiBase()}/open/apps/${encodeURIComponent(appId)}/content/${parsed}`, { headers: { accept: "application/json" } });
  const payload = await response.json() as { success?: boolean; data?: PublishedApplicationDocument; error?: { message?: string } };
  if (!response.ok || !payload.success || !payload.data) throw new Error(payload.error?.message ?? "协议不存在或未发布");
  const document = publishedApplicationDocumentSchema.parse(payload.data);
  if (document.appId !== appId || document.type !== parsed) throw new Error("协议所属应用或类型不匹配");
  return document;
}

export function PublicDocumentPage() {
  const { appId = "", type = "" } = useParams();
  const [params, setParams] = useSearchParams();
  const locale = useMemo<AppContentLocale>(() => params.get("lang") === "ug" ? "ug" : "zh", [params]);
  const runtime = useAdminRuntime();
  const contentApi = useOptionalContentApi();
  const parsed = applicationDocumentTypeSchema.safeParse(type);
  const query = useQuery({
    queryKey: ["public-document", appId, type, runtime.mode],
    enabled: Boolean(appId) && parsed.success,
    retry: false,
    queryFn: async () => {
      const document = contentApi ? await contentApi.getPublished(appId, type) : await fetchPublishedDocument(appId, type);
      if (document.appId !== appId || document.type !== type) throw new Error("协议所属应用或类型不匹配");
      return document;
    },
  });
  const title = parsed.success ? legalTypes[parsed.data] : "协议";
  if (!parsed.success) return <Result status="warning" title="不支持的协议类型" />;
  if (query.isPending) return <main className="public-document" aria-busy="true"><Spin size="large" /></main>;
  if (query.error) return <main className="public-document"><Alert type="error" showIcon title="无法打开协议" description={query.error.message} action={<Button onClick={() => void query.refetch()}>重试</Button>} /></main>;
  const document = query.data!;
  const copy = pickLocaleCopy(document, locale);
  return <main className="public-document" aria-labelledby="public-document-title" lang={locale === "ug" ? "ug" : "zh"} dir={locale === "ug" ? "rtl" : "ltr"}>
    <PublicLocaleSwitch locale={locale} onChange={(next) => { const updated = new URLSearchParams(params); updated.set("lang", next); setParams(updated, { replace: true }); }} />
    <p className="public-document-kicker">{title} · v{document.version}</p>
    <h1 id="public-document-title">{copy.title}</h1>
    <p className="public-document-meta">发布于 {new Date(document.publishedAt).toLocaleString("zh-CN", { timeZone: "Asia/Shanghai", hour12: false })}（北京时间）</p>
    <pre className="public-document-body">{copy.body}</pre>
  </main>;
}
export default PublicDocumentPage;
